import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CurriculaService } from "../../../Axios/services/curricula.service";
import CurriculumFolder from "../../content/Curricula/CurriculumFolder/CurriculumFolder";
import CurriculumCI from "../../content/Curricula/CurriculumCI/CurriculumCI";
import copyIcon from "../../../assets/images/copy.svg";
import { Folder, ICurricula, TabItem } from "../../../models/curricula";
import { FACULTY_ONLY, NONE } from "../../../config/keys";
import { showToast } from "../../../store/actions/toastActions";
import CurriculumSkeleton from "../../content/Curricula/CurriculumSkeleton/CurriculumSkeleton";
import styles from "./CurriculumPage.module.scss";
import "./CurriculumPage.scss"; // TBR i think is not working

interface GroupedFolders {
  common: Folder[];
  tabs: Map<string, Folder[]>;
}

const CurriculumPage = () => {
  const navigate = useNavigate();
  const curriculaService = CurriculaService();
  const { id } = useParams();
  const { me } = useSelector((state: any) => state.auth);
  const [curriculum, setCurriculum] = useState<ICurricula | null>(null);
  const [tabs, setTabs] = useState<TabItem[]>([]);
  const [activeTab, setActiveTab] = useState<TabItem | null>(null);
  const [entityLanguage, setEntityLanguage] = useState<string>("common");
  const [loading, setLoading] = useState<boolean>(true);
  const [t] = useTranslation(entityLanguage);
  const isEnglishLanguage = entityLanguage === "English";
  const [mapFolders, setMapFolder] = useState<GroupedFolders | null>(null);
  const dispatch = useDispatch();
  const registrationURL = useMemo(() => {
    if (curriculum?.curriculaUsers && curriculum.registrationURL) {
      const isParticipant = curriculum.curriculaUsers.find((user) => user._id === me._id && user.curricula[0].entityRole?.includes("participant"));
      return !isParticipant && `${curriculum?.registrationURL}?contact=${me?.contactSFId}&course=${curriculum?.sfSeminarId}`;
    }
  }, [curriculum?.curriculaUsers, curriculum?.registrationURL, curriculum?.sfSeminarId, me?.contactSFId, me?._id]);

  useEffect(() => {
    document.body.scrollTop = 0;
    if (id) getCurriculaById(id);
  }, [id]);

  const getCurriculaById = async (entityId: string) => {
    try {
      setLoading(true);
      const curricula = await curriculaService.getCurriculaByIdNew(entityId);
      if (curricula) {
        const curriculaParsed = updateCurriculaSettings(curricula);
        setCurriculum(curriculaParsed);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const updateCurriculaSettings = (curriculaData) => {
    try {
      const curricula: ICurricula = curriculaData.data[0];
      const entityTemplatesData = curricula.entityTemplatesData[0];

      setEntityLanguage(entityTemplatesData && entityTemplatesData.language);
      localStorage.setItem("entityLanguage", entityTemplatesData && entityTemplatesData.language);

      if (entityTemplatesData?.tabItems && entityTemplatesData.tabItems.length > 0) {
        const isParticipant = curricula.curriculaUsers?.find((user) => user?._id == me?._id && user.curricula[0]?.entityRole?.includes("participant"));
        const filteredTab = entityTemplatesData?.tabItems
          .filter((tab) => {
            if (isParticipant && tab.permission) {
              return !tab.permission.includes(FACULTY_ONLY) && !tab.permission.includes(NONE);
            }
            return true;
          })
          .sort((a, b) => a.order - b.order);
        setTabs(filteredTab);
        setActiveTab(filteredTab[0]);
      }

      const groupeFolders = groupFoldersByTabs(curricula, entityTemplatesData?.tabItems);
      setMapFolder(groupeFolders);
      return curricula;
    } catch (err) {
      console.error(err);
    }
  };

  const groupFoldersByTabs = (curricula: ICurricula, customTabs: TabItem[]): GroupedFolders => {
    let folders = curricula.folderList;
    const grouped: GroupedFolders = {
      common: [],
      tabs: new Map<string, Folder[]>(),
    };

    folders
      .filter((folder) => folder.status === "Active")
      .forEach((folder) => {
        if (folder.permissions[0] === "OpenFaculty" || folder.permissions[0] === "OpenStudents") {
          grouped.common.push(folder);
        } else {
          if (folder.customTabs && folder.customTabs.length > 0) {
            folder.customTabs.forEach((tab) => {
              if (tab.tabSelected && tab.templateId === curricula.entityTemplatesData[0]._id) {
                if (!grouped.tabs.has(tab.tabTitle)) {
                  grouped.tabs.set(tab.tabTitle, []);
                }
                grouped.tabs.get(tab.tabTitle)!.push(folder);
              }
            });
          }
        }
      });

    // Sort folders in each tab by their order
    const folderItemsMap = new Map(curricula.entityTemplatesData[0]?.folderItems?.map(item => [item._id, item.order]) || []);
    
    // Sort common folders
    grouped.common.sort((a, b) => {
      const orderA = folderItemsMap.get(a._id) ?? 999;
      const orderB = folderItemsMap.get(b._id) ?? 999;
      return orderA - orderB;
    });

    // Sort folders in each tab
    grouped.tabs.forEach((folderArray, tabTitle) => {
      folderArray.sort((a, b) => {
        const orderA = folderItemsMap.get(a._id) ?? 999;
        const orderB = folderItemsMap.get(b._id) ?? 999;
        return orderA - orderB;
      });
    });

    return grouped;
  };

  const toggleTab = (e, tab) => {
    e.preventDefault();
    setActiveTab(tab);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    dispatch(showToast({ message: "Link copied to clipboard", open: true, severity: "success" }));
  };

  return (
    <>
      {loading ? (
        <CurriculumSkeleton />
      ) : curriculum?.entityTemplatesData?.length ? (
        <div className={`curriculumPage container-fluid`} dir={isEnglishLanguage ? "ltr" : "rtl"} key={curriculum.entityTemplatesData[0]._id}>
          <div className="goBack" onClick={() => navigate('/curricula')}>
            {t("Back to list")}
          </div>
          <div>
            <div className="d-flex flex-column" dir={isEnglishLanguage ? "ltr" : "rtl"}>
              {curriculum.entityTemplatesData?.[0].thumbnail && (
                <div className="curriculumThumbContainer">
                  <img alt={curriculum.entityTemplatesData[0].title} src={curriculum.entityTemplatesData[0].thumbnail} className="curriculumThumb" />{" "}
                </div>
              )}
              <div className={`curriculumTitle ${isEnglishLanguage ? null : "alignRight"}`}>{curriculum.entityTemplatesData[0].title}</div>
              <div className={`curriculumDescription ${isEnglishLanguage ? null : "alignRight"}`}>{curriculum.entityTemplatesData[0].description}</div>
            </div>
            <div className="d-flex flex-column folderOpenedWrapper mb-3">
              {mapFolders.common.length > 0
                ? mapFolders.common.map((folder: Folder) => (
                    <div className="fileListWrapper" key={folder._id}>
                      {folder.contentItems.map((item: any) => {
                        return <CurriculumCI item={item} mode={"outsideFolder"} key={item._id}/>;
                      })}
                    </div>
                  ))
                : null}
            </div>

            {registrationURL && (
              <div className={styles.linkContainer}>
                <p className={styles.linkTitle}>Share this link to invite your students to access the learner materials on Hartman Connect</p>
                <div className={styles.linkBox} onClick={() => copyToClipboard(registrationURL)}>
                  <input type="text" className={styles.linkInput} value={registrationURL} readOnly />
                  <img className={styles.copyIcon} src={copyIcon} alt="copy icon" />
                </div>
              </div>
            )}

            {tabs && tabs.length > 0 && (
              <>
                <div className="d-flex NavContainer">
                  {tabs.map((tab: TabItem) => (
                    <div
                      key={tab.title + tab.order}
                      className={`${tab.title === activeTab.title ? "mr-2 folderFilterBtn ActiveBtn" : "mr-2 folderFilterBtn"} ${isEnglishLanguage ? null : "alignRight"}`}
                      onClick={(e) => toggleTab(e, tab)}
                    >
                      {tab.title}
                    </div>
                  ))}
                </div>
                <div>
                  {mapFolders.tabs.get(activeTab.title)?.map((folder: Folder) => {
                    return <CurriculumFolder item={folder} language={entityLanguage} key={folder._id}/>;
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div>No data</div>
      )}
    </>
  );
};

export default CurriculumPage;
